import React from 'react';
import { AaPanel } from '@openadp/adp-assist-mfe-react';
import { SdfIcon, SdfIconButton } from '@waypoint/react-components';
import { WfnMfeLoader } from '@adp-wfn/mdf-wc-react';

export interface IMDFAssistLayoutProps {
  // The configuration object passed through to AaPanel.
  assistConfig?: Record<string, any>;

  children?: React.ReactNode;

  // A CSS class name for overriding styles on the root element.
  className?: string;

  // Called when the user closes the assist panel.
  onAssistClose?: () => void;

  // Declaratively show or hide the assist panel.
  showAssist?: boolean;
}

export const MDFAssistLayout = (props: IMDFAssistLayoutProps) => {
  const { assistConfig, children, className, onAssistClose, showAssist } = props;
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = rootRef.current;

    if (!el) {
      return;
    }

    let attempts = 0;
    let rafId: number;
    const maxAttempts = 10;

    // Poll until the element has a non-zero top position (i.e., the modal
    // has positioned its content) or we've exhausted our attempts.
    function measureWhenReady() {
      const top = el.getBoundingClientRect().top;
      attempts++;

      if (top === 0 && attempts < maxAttempts) {
        rafId = requestAnimationFrame(measureWhenReady);
        return;
      }

      let bottomOffset = 20;

      // Walk up the DOM to find a focus-pane ancestor. If it has a visible
      // footer (hide-footer is not set), we need extra space for it.
      let parent = el.parentElement;

      while (parent) {
        if (parent.tagName.toLowerCase().includes('focus-pane')) {
          bottomOffset = parent.hasAttribute('hide-footer') ? 20 : 92;
          break;
        }

        parent = parent.parentElement;
      }

      el.style.maxHeight = `calc(100vh - ${top + bottomOffset}px)`;
    }

    // Delay initial measurement to allow the modal's slide-in animation to start.
    const timerId = setTimeout(() => {
      rafId = requestAnimationFrame(measureWhenReady);
    }, 250);

    return () => {
      clearTimeout(timerId);
      cancelAnimationFrame(rafId);
    };
  }, []);

  let rootClassName = 'mdf-assist-layout';

  if (className) {
    rootClassName += ' ' + className;
  }

  const hasValidConfig = !!assistConfig?.assistType;

  return (
    <div className={rootClassName} ref={rootRef}>
      <WfnMfeLoader
        base-url="/adp-assist-mfe"
        config-object="AAConfig"
        loader-uri="/config/config.js"
        no-cors
      />
      <div className="mdf-assist-layout__content">
        {children}
      </div>
      {showAssist && hasValidConfig && (
        <div className="mdf-assist-layout__assist">
          <div className="mdf-assist-layout__assist-header">
            <span className="mdf-assist-layout__assist-title">ADP Assist</span>
            <SdfIconButton label="Close" onClick={onAssistClose}>
              <SdfIcon icon="action-close" />
            </SdfIconButton>
          </div>
          <AaPanel
            assistConfig={assistConfig}
            assistValue={assistConfig.assistType}
            embedded={true}
            floating={false}
            onAaClose={onAssistClose}
            spokeName={assistConfig.spokeName}
            visible={true}
            panelHeight={'h-full'}
          />
        </div>
      )}
    </div>
  );
};

MDFAssistLayout.displayName = 'MDFAssistLayout';
